import type {
  OrchestrationImportProviderSessionInput,
  OrchestrationListImportableProviderSessionsInput,
  OrchestrationListImportableProviderSessionsResult,
} from "@t3tools/contracts";
import type { ProviderSessionImportError } from "@t3tools/contracts";
import * as Effect from "effect/Effect";

import type { EnvironmentSupervisor } from "../connection/supervisor.ts";
import {
  type EnvironmentRpcFailure,
  type EnvironmentRpcSuccess,
  type EnvironmentRpcUnavailableError,
  request,
} from "../rpc/client.ts";
import { ORCHESTRATION_WS_METHODS } from "@t3tools/contracts";

export type ProviderSessionImportItem =
  OrchestrationListImportableProviderSessionsResult["items"][number];
export type ProviderSessionImportSortOrder = "recent" | "oldest";
export type ProviderSessionImportTimeRange = "all" | "7d" | "30d" | "90d";
export type ProviderSessionImportAction = "import" | "navigate" | "disabled";

export function providerSessionImportErrorReason(
  error: unknown,
): ProviderSessionImportError["reason"] | null {
  if (
    typeof error !== "object" ||
    error === null ||
    !("_tag" in error) ||
    error._tag !== "ProviderSessionImportError" ||
    !("reason" in error)
  ) {
    return null;
  }
  switch (error.reason) {
    case "project-not-found":
    case "provider-unavailable":
    case "import-unsupported":
    case "configuration-conflict":
    case "candidate-invalid":
    case "candidate-stale":
    case "session-unavailable":
    case "session-busy":
    case "confirmation-required":
    case "deleting-conflict":
    case "history-unavailable":
    case "persistence-failed":
      return error.reason;
    default:
      return null;
  }
}

export interface ProviderSessionImportPageState {
  readonly items: ReadonlyArray<ProviderSessionImportItem>;
  readonly nextCursor?: string;
}

export function appendProviderSessionImportPage(input: {
  readonly current: ProviderSessionImportPageState;
  readonly requestedCursor?: string;
  readonly page: OrchestrationListImportableProviderSessionsResult;
}): ProviderSessionImportPageState {
  const seen = new Set(input.current.items.map((item) => item.candidateRef));
  const items = [...input.current.items];
  for (const item of input.page.items) {
    if (seen.has(item.candidateRef)) continue;
    seen.add(item.candidateRef);
    items.push(item);
  }
  const nextCursor =
    input.page.cursor !== undefined && input.page.cursor !== input.requestedCursor
      ? input.page.cursor
      : undefined;
  return { items, ...(nextCursor === undefined ? {} : { nextCursor }) };
}

export function providerSessionImportAction(
  item: ProviderSessionImportItem,
): ProviderSessionImportAction {
  if (item.availability === "already-imported" && item.existingThreadId !== undefined) {
    return "navigate";
  }
  if (
    item.availability === "available" ||
    item.availability === "activity-unknown" ||
    item.availability === "archived"
  ) {
    return "import";
  }
  return "disabled";
}

export function filterAndSortProviderSessionImportItems(input: {
  readonly items: ReadonlyArray<ProviderSessionImportItem>;
  readonly query: string;
  readonly sortOrder: ProviderSessionImportSortOrder;
  readonly timeRange?: ProviderSessionImportTimeRange;
  readonly now?: number;
}): ReadonlyArray<ProviderSessionImportItem> {
  const query = input.query.trim().toLocaleLowerCase();
  const maximumAgeDays =
    input.timeRange === "7d"
      ? 7
      : input.timeRange === "30d"
        ? 30
        : input.timeRange === "90d"
          ? 90
          : null;
  const oldestIncludedAt =
    maximumAgeDays === null || input.now === undefined
      ? null
      : input.now - maximumAgeDays * 86_400_000;
  const filtered = input.items.filter(
    (item) =>
      (query.length === 0 || item.title.toLocaleLowerCase().includes(query)) &&
      (oldestIncludedAt === null || Date.parse(item.lastActivityAt) >= oldestIncludedAt),
  );
  const direction = input.sortOrder === "recent" ? -1 : 1;
  return filtered.sort((left, right) => {
    const byActivity = left.lastActivityAt.localeCompare(right.lastActivityAt) * direction;
    return byActivity === 0 ? left.title.localeCompare(right.title) : byActivity;
  });
}

export function providerSessionImportNeedsActivityAcknowledgement(
  item: ProviderSessionImportItem | null,
): boolean {
  return item?.requiresActivityAcknowledgement === true;
}

export function providerSessionImportCanSubmit(input: {
  readonly item: ProviderSessionImportItem | null;
  readonly activityAcknowledged: boolean;
  readonly submitting: boolean;
}): boolean {
  if (input.submitting || input.item === null) return false;
  if (providerSessionImportAction(input.item) !== "import") {
    return false;
  }
  return !input.item.requiresActivityAcknowledgement || input.activityAcknowledged;
}

type ListTag = typeof ORCHESTRATION_WS_METHODS.listImportableProviderSessions;
type ImportTag = typeof ORCHESTRATION_WS_METHODS.importProviderSession;

export const listImportableProviderSessions: (
  input: OrchestrationListImportableProviderSessionsInput,
) => Effect.Effect<
  EnvironmentRpcSuccess<ListTag>,
  EnvironmentRpcFailure<ListTag> | EnvironmentRpcUnavailableError,
  EnvironmentSupervisor
> = Effect.fn("ProviderSessionImport.list")(function* (input) {
  return yield* request(ORCHESTRATION_WS_METHODS.listImportableProviderSessions, input);
});

export const importProviderSession: (
  input: OrchestrationImportProviderSessionInput,
) => Effect.Effect<
  EnvironmentRpcSuccess<ImportTag>,
  EnvironmentRpcFailure<ImportTag> | EnvironmentRpcUnavailableError,
  EnvironmentSupervisor
> = Effect.fn("ProviderSessionImport.import")(function* (input) {
  return yield* request(ORCHESTRATION_WS_METHODS.importProviderSession, input);
});
